function asFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

/**
 * Normalize API/Mongo location shapes into a Google Maps LatLngLiteral.
 * Accepts `{ lat, lng }`, numeric strings, and GeoJSON Points.
 */
export function toLatLngLiteral(
  value: unknown,
): { lat: number; lng: number } | null {
  if (value == null || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;

  const lat = asFiniteNumber(record.lat);
  const lng = asFiniteNumber(record.lng);
  if (lat != null && lng != null) return { lat, lng };

  const coordinates = record.coordinates;
  if (Array.isArray(coordinates) && coordinates.length >= 2) {
    const geoLng = asFiniteNumber(coordinates[0]);
    const geoLat = asFiniteNumber(coordinates[1]);
    if (geoLat != null && geoLng != null) return { lat: geoLat, lng: geoLng };
  }

  return null;
}

export function haversineDistanceMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
) {
  const toRadians = (value: number) => (value * Math.PI) / 180;
  const earthRadiusMeters = 6371000;
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);

  const haversine =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);

  return (
    2 *
    earthRadiusMeters *
    Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  );
}

export function sortByDistanceFromLocation<
  T extends { location: { lat: number; lng: number } },
>(items: T[], location: { lat: number; lng: number }) {
  return [...items].sort((a, b) => {
    return (
      haversineDistanceMeters(a.location, location) -
      haversineDistanceMeters(b.location, location)
    );
  });
}
